import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Link from 'components/atoms/Link';
import InlineDropdown from 'components/molecules/InlineDropdown';
import { appendQueryParams } from 'utils/bundle-helper';
import { translate } from 'utils/translate';

import Toggle from 'components/utilities/Toggle';
import Heading, { H1 } from 'components/atoms/Heading';
import { minus, plus } from 'components/atoms/Icon/icons';
import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import FilterGroup from './FilterGroup';

import * as styles from './ProductFilter.scss';
// add more if needed
const TRANSLATED_NAMES = {
  AreaIds: '/pages/eventListPage/area',
  AwardIds: '/product/ecoCertyficate',
  CategoryIds: '/pages/productListPage/category',
  EventCategoryIds: '/pages/eventListPage/category',
};

const ProductFilter = ({
  name,
  filters,
  filterType,
  toggleable,
  open,
  url,
  onToggle,
  className,
  dropdown = true,
  filterGroupChar,
  filterPage,
  filterGroup,
  color,
  compact = false,
  onSetFiltersBtnClick,
}) => {
  // if filterType === "regionFilter", find name from translations
  if (
    (!filterGroup && (!filters || filters === undefined)) ||
    (filterGroup && filterGroup === undefined)
  ) {
    return null;
  }

  const eventListView = filterPage === 'Event';

  let hideIndex = 5;

  const style = {};
  if (color) {
    style['--wrapperColor'] = `${color}66`;
  }

  const label = TRANSLATED_NAMES[filterType]
    ? translate(TRANSLATED_NAMES[filterType])
    : name;

  const [filtersActive, setFiltersActive] = useState({ ...url.query });
  const [filtersChanged, setFiltersChanged] = useState(false);

  useEffect(() => {
    setFiltersChanged(
      JSON.stringify(url.query) !== JSON.stringify(filtersActive),
    );
  }, [url.query, filtersActive]);

  if (filterGroup) {
    const filtered = Object.keys(url.query).some(key =>
      Object.keys(filterGroup).includes(key),
    );
    const useSelectedFiltersQuery = useMemo(
      () =>
        Object.keys(url.query).reduce(
          (result, key) => {
            const res = { ...result };
            res[key] =
              Object.hasOwn(filtersActive, key) && filtersActive[key].length > 0
                ? filtersActive[key]
                : '';
            return result;
          },
          { ...url.query, ...filtersActive },
        ),
      [filtersActive],
    );

    return (
      <div className={styles.eventFilter}>
        <div className={styles.filters} style={style}>
          {Object.values(filterGroup).map((group, index) => (
            <FilterGroup
              key={group.filterType}
              group={group}
              open={open}
              onToggle={onToggle}
              filterPage={filterPage}
              filterGroupChar={filterGroupChar}
              compact={compact}
              url={url}
              index={index}
              handleSetFilter={setFiltersActive}
              filtersActive={filtersActive}
            />
          ))}
        </div>

        {compact ? (
          <div className={styles.buttonWrapper}>
            <Button
              className={classnames(styles.useFiltersButton, {
                [styles.active]: filtersChanged,
              })}
              palette="secondary"
              href={appendQueryParams(url, useSelectedFiltersQuery, true).href}
              onClick={onSetFiltersBtnClick}
              disabled={!filtersChanged}
            >
              {translate('/pages/eventListPage/useSelectedFilters')}
            </Button>

            <Button
              className={classnames(styles.resetFiltersButton, {
                [styles.active]: filtered,
              })}
              palette="secondary"
              href={
                appendQueryParams(
                  url,
                  Object.keys(filterGroup).reduce((f, key) => {
                    f[key] = '';
                    return f;
                  }, {}),
                  true,
                ).href
              }
              onClick={onSetFiltersBtnClick}
              disabled={!filtered}
            >
              {translate('/pages/eventListPage/resetFilters')}
            </Button>
          </div>
        ) : (
          <Button
            className={classnames(styles.resetFiltersButton, {
              [styles.active]: filtered,
            })}
            palette="secondary"
            href={
              appendQueryParams(
                url,
                Object.keys(filterGroup).reduce((f, key) => {
                  f[key] = '';
                  return f;
                }, {}),
                true,
              ).href
            }
            disabled={!filtered}
          >
            {translate('/pages/eventListPage/resetFilters')}
          </Button>
        )}
      </div>
    );
  }

  const getIsFiltered = (filter, queryArray) => {
    let isFiltered = false;
    if (Object.prototype.hasOwnProperty.call(filter, 'tellusCategoryIds')) {
      // Some filters contains the same tellusCategoryIds, and were therefore falsely set as isFiltered.
      // Set specifically to the eventListView to prevent possibly breaking something elsewhere.
      if (eventListView) {
        if (filter.tellusCategoryIds.every(id => queryArray.includes(id)))
          isFiltered = true;
      } else {
        queryArray.forEach(q => {
          if (filter.tellusCategoryIds.includes(q)) isFiltered = true;
        });
      }
    } else {
      isFiltered = queryArray.includes(filter.id);
    }
    return isFiltered;
  };

  const getNewQueryArray = (filter, isFiltered) => {
    if (Object.prototype.hasOwnProperty.call(filter, 'tellusCategoryIds')) {
      return isFiltered
        ? queryArray.filter(id => !filter.tellusCategoryIds.includes(id))
        : [...queryArray, ...filter.tellusCategoryIds];
    }
    return isFiltered
      ? queryArray.filter(id => id !== filter.id)
      : [...queryArray, filter.id];
  };

  // eslint-disable-next-line react/prop-types
  const isOrFilter = filterType.startsWith('Or');
  const allFilters = isOrFilter
    ? filters.map(f => ({ ...f, id: filterGroupChar + f.id }))
    : filters.map(f => ({ ...f, id: f.id.toString() }));
  const queryArray = (url.query[filterType] || '')
    .split(',')
    // split returns empty nodes for an empty string
    .filter(item => item !== '');
  if (dropdown) {
    return (
      <InlineDropdown
        label={label}
        open={open}
        onChange={onToggle}
        toggleable={toggleable}
        className={classnames(styles.root, styles.dropdown, className)}
      >
        <ul className={styles.list}>
          {allFilters.map(filter => {
            const isFiltered = getIsFiltered(filter, queryArray);
            const newQueryArray = getNewQueryArray(filter, isFiltered);
            return (
              <li className={styles.item} key={filter.id}>
                <Link
                  className={classnames(styles.link, {
                    [styles.selected]: isFiltered,
                  })}
                  href={
                    appendQueryParams(
                      url,
                      {
                        [filterType]: newQueryArray.join(','),
                      },
                      true,
                    ).href
                  }
                  data-no-scrolltop
                >
                  {filter.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </InlineDropdown>
    );
  }

  if (!toggleable)
    return (
      <section className={classnames(styles.root, className)}>
        <H1 size={Heading.sizes.heading4} className={styles.heading}>
          {label}
        </H1>
        <ul className={styles.list}>
          {allFilters.map(filter => {
            const isFiltered = getIsFiltered(filter, queryArray);
            const newQueryArray = getNewQueryArray(filter, isFiltered);
            return (
              <li className={styles.item} key={filter.id}>
                <Link
                  className={classnames(styles.link, {
                    [styles.selected]: isFiltered,
                  })}
                  href={
                    appendQueryParams(
                      url,
                      {
                        [filterType]: newQueryArray.join(','),
                      },
                      true,
                    ).href
                  }
                  data-no-scrolltop
                >
                  {filter.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    );

  hideIndex = 5;

  return (
    <section className={classnames(styles.root, className)}>
      <H1 size={Heading.sizes.heading4} className={styles.heading}>
        {label}
      </H1>
      <ul className={styles.list}>
        <Toggle
          on={false}
          render={({ on, toggle }) => (
            <React.Fragment>
              {allFilters.map((filter, index) => {
                const isFiltered = getIsFiltered(filter, queryArray);
                const newQueryArray = getNewQueryArray(filter, isFiltered);
                return (
                  index < hideIndex && (
                    <li className={styles.item} key={filter.id}>
                      <Link
                        className={classnames(styles.link, {
                          [styles.selected]: isFiltered,
                        })}
                        href={
                          appendQueryParams(
                            url,
                            {
                              [filterType]: newQueryArray.join(','),
                            },
                            true,
                          ).href
                        }
                        data-no-scrolltop
                      >
                        {filter.name}
                      </Link>
                    </li>
                  )
                );
              })}
              {on &&
                allFilters.map((filter, index) => {
                  const isFiltered = getIsFiltered(filter, queryArray);
                  const newQueryArray = getNewQueryArray(filter, isFiltered);
                  return (
                    index >= hideIndex && (
                      <li className={styles.item} key={filter.id}>
                        <Link
                          className={classnames(styles.link, {
                            [styles.selected]: isFiltered,
                          })}
                          href={
                            appendQueryParams(
                              url,
                              {
                                [filterType]: newQueryArray.join(','),
                              },
                              true,
                            ).href
                          }
                          data-no-scrolltop
                        >
                          {filter.name}
                        </Link>
                      </li>
                    )
                  );
                })}

              <li>
                <Button
                  transparent
                  onClick={toggle}
                  className={styles.toggle}
                  noPadding
                >
                  <Icon icon={on ? minus : plus} />
                  {on
                    ? translate('/pages/productListPage/showLess')
                    : translate('/pages/productListPage/showMore')}
                </Button>
              </li>
            </React.Fragment>
          )}
        />
      </ul>
    </section>
  );
};
ProductFilter.displayName = 'ProductFilter';
ProductFilter.propTypes = {
  filterGroup: PropTypes.objectOf(
    PropTypes.shape({
      filterType: PropTypes.string.isRequired,
      filters: PropTypes.any.isRequired,
      toggleable: PropTypes.bool,
      dropdown: PropTypes.bool,
    }),
  ),
};
ProductFilter.defaultProps = {};

export default ProductFilter;
